import Layout from "../Layout";
import { Link } from "@inertiajs/react";
import {
  Mail,
  Shield,
  MessageSquare,
  Clock,
  Wrench,
  FileText,
  CreditCard,
  AlertTriangle,
} from "lucide-react";

interface ContactChannel {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  email: string;
  description: string;
  links?: ReadonlyArray<{ href: string; label: string }>;
}

const CONTACT_CHANNELS: ReadonlyArray<ContactChannel> = [
  {
    icon: Mail,
    title: "Product & billing",
    email: "support@infralyst.io",
    description:
      "Questions about setup, recommendations, Terraform PRs, or billing. Email us from the address linked to your workspace and include any relevant screenshots, error messages, or PR links.",
  },
  {
    icon: Shield,
    title: "Security & privacy",
    email: "security@infralyst.io",
    description:
      "Found a potential security or privacy issue, or have questions about how we handle data? Email us.",
    links: [
      { href: "/security", label: "Security" },
      { href: "/privacy-policy", label: "Privacy Policy" },
    ],
  },
  {
    icon: MessageSquare,
    title: "General questions",
    email: "hello@infralyst.io",
    description:
      "Not sure if Infralyst is a good fit yet, or want to talk about roadmap and partnerships? Drop us a line.",
  },
];

const HELP_TOPICS = [
  {
    icon: Wrench,
    title: "Setup & onboarding",
    description:
      "Connecting AWS accounts via read-only IAM roles, installing the GitHub App, and mapping Terraform state to the right accounts and repos.",
  },
  {
    icon: FileText,
    title: "Recommendations & PRs",
    description:
      "Understanding why a recommendation was made or skipped, and troubleshooting Terraform PR creation and status. AI agents handle only the Terraform code changes, not the analysis.",
  },
  {
    icon: AlertTriangle,
    title: "Safety & downtime warnings",
    description:
      "Questions about warnings on specific recommendations: downtime requirements, safety checks on specific resources, or why a particular resource isn't eligible for a recommendation.",
  },
  {
    icon: CreditCard,
    title: "Account, billing & notifications",
    description:
      "Plan changes, invoices, and workspace management. Also covers Slack notification setup, channel configuration, and alert frequency.",
  },
] as const;

const RESPONSE_INFO = [
  {
    icon: Clock,
    title: "Hours",
    description:
      "We usually reply Monday–Friday during Hong Kong and UK business hours.",
  },
  {
    icon: Mail,
    title: "Typical response time",
    description:
      "Most emails get a reply within 1–2 business days.",
  },
  {
    icon: Shield,
    title: "Priority issues",
    description:
      "If something looks production-impacting (for example, PRs failing to generate across your workspace), mention it in your subject line and we'll prioritize it.",
  },
] as const;

function SupportHero(): React.ReactElement {
  return (
    <section className="px-4 py-16 pt-40">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="mb-4 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
          <span className="text-primary">Infralyst</span> Support
        </h1>
        <p className="text-2xl font-medium text-surface-foreground/90 mb-4">
          Questions answered by the engineers building it
        </p>
        <p className="max-w-2xl mx-auto text-lg leading-relaxed text-surface-foreground/80">
          The fastest way to reach us is{" "}
          <a
            href="mailto:support@infralyst.io"
            className="text-primary hover:underline"
          >
            support@infralyst.io
          </a>
          . Email from the address linked to your workspace and include any
          relevant screenshots, error messages, or PR links.
        </p>
      </div>
    </section>
  );
}

function HowToGetHelp(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          How to get help
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {CONTACT_CHANNELS.map(
            ({ icon: Icon, title, email, description, links }) => (
              <div
                key={title}
                className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
              >
                <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <Icon className="size-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{title}</h3>
                <a
                  href={`mailto:${email}`}
                  className="text-primary hover:underline font-medium mb-3 block"
                >
                  {email}
                </a>
                <p className="text-surface-foreground/80 text-sm leading-relaxed">
                  {description}
                </p>
                {links && (
                  <p className="mt-3 text-sm text-surface-foreground/80">
                    You can also read more on our{" "}
                    {links.map((link, index) => (
                      <span key={link.href}>
                        <Link
                          href={link.href}
                          className="text-primary hover:underline"
                        >
                          {link.label}
                        </Link>
                        {index < links.length - 1 && " and "}
                      </span>
                    ))}{" "}
                    pages.
                  </p>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
}

function WhatWeCanHelpWith(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          What we can help you with
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {HELP_TOPICS.map(({ icon: Icon, title, description }) => (
            <div key={title} className="flex gap-4">
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Icon className="size-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold mb-1">{title}</h3>
                <p className="text-sm text-surface-foreground/80 leading-relaxed">
                  {description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ResponseTimes(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Response times & availability
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          No ticket queues or scripted replies. Engineers respond directly.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {RESPONSE_INFO.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon className="size-6" />
              </div>
              <h3 className="text-xl font-bold mb-3">{title}</h3>
              <p className="text-surface-foreground/80 text-sm leading-relaxed">
                {description}
              </p>
            </div>
          ))}
        </div>

        <p className="text-center text-surface-foreground/60 mt-12 max-w-xl mx-auto">
          Not sure where your question fits?{" "}
          <a
            href="mailto:support@infralyst.io"
            className="text-primary hover:underline"
          >
            Email support@infralyst.io
          </a>{" "}
          and the right engineer will pick it up.
        </p>
      </div>
    </section>
  );
}

export default function Show(): React.ReactElement {
  return (
    <Layout
      title="Support"
      description="Get help with Infralyst. Contact our engineering team for support with setup, savings recommendations, Terraform PRs, or billing."
    >
      <SupportHero />
      <HowToGetHelp />
      <WhatWeCanHelpWith />
      <ResponseTimes />
    </Layout>
  );
}
